import React, { useState, useEffect } from 'react';
import api from '../api';
import ConfirmDeleteModal from './ConfirmDeleteModal';

interface Attribute {
  id: number;
  name: string;
  type: string;
  options?: string[];
  required: boolean;
  category_id?: number;
}

interface Category {
  id: number;
  name: string;
}

const AttributeSetup: React.FC = () => {
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{show: boolean; id: number | null; name: string}>({show: false, id: null, name: ''});
  const [formData, setFormData] = useState({
    name: '',
    type: 'text',
    options: '',
    required: false,
    category_id: ''
  });

  useEffect(() => {
    fetchAttributes();
    fetchCategories();
  }, []);

  const fetchAttributes = async () => {
    try {
      const response = await api.get('/attributes/');
      setAttributes(response.data);
    } catch (error) {
      console.error('Error fetching attributes:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories/');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const attributeData = {
        ...formData,
        options: formData.options ? formData.options.split(',').map(s => s.trim()) : null,
        category_id: formData.category_id ? parseInt(formData.category_id) : null
      };
      
      if (editingId) {
        await api.put(`/attributes/${editingId}`, attributeData);
        alert('Attribute updated successfully');
      } else {
        await api.post('/attributes/', attributeData);
        alert('Attribute created successfully');
      }
      
      setFormData({ name: '', type: 'text', options: '', required: false, category_id: '' });
      setEditingId(null);
      setShowForm(false);
      fetchAttributes();
    } catch (error: any) {
      console.error('Error saving attribute:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to save attribute';
      alert(errorMsg);
    }
  };

  const handleEdit = (attribute: Attribute) => {
    setFormData({
      name: attribute.name,
      type: attribute.type,
      options: attribute.options ? attribute.options.join(', ') : '',
      required: attribute.required,
      category_id: attribute.category_id ? attribute.category_id.toString() : ''
    });
    setEditingId(attribute.id);
    setShowForm(true);
  };

  const handleCancelEdit = () => {
    setFormData({ name: '', type: 'text', options: '', required: false, category_id: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleDelete = async (id: number, name?: string) => {
    const attr = attributes.find(a => a.id === id);
    setDeleteConfirm({show: true, id, name: name || attr?.name || `Attribute #${id}`});
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.id) return;
    try {
      await api.delete(`/attributes/${deleteConfirm.id}`);
      setDeleteConfirm({show: false, id: null, name: ''});
      fetchAttributes();
    } catch (error: any) {
      console.error('Error deleting attribute:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete attribute';
      alert(errorMsg);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(attributes.map(a => a.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectItem = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(i => i !== id));
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedIds.length > 1) {
      setShowBulkDeleteConfirm(true);
    } else if (selectedIds.length === 1) {
      handleDelete(selectedIds[0]);
      setSelectedIds([]);
    }
  };

  const confirmBulkDelete = async () => {
    if (confirmText !== 'confirm') {
      return;
    }

    const errors: string[] = [];
    for (const id of selectedIds) {
      try {
        await api.delete(`/attributes/${id}`);
      } catch (error: any) {
        const errorMsg = error.response?.data?.detail || 'Failed to delete';
        errors.push(`Attribute ${id}: ${errorMsg}`);
      }
    }

    if (errors.length > 0) {
      alert(`Some deletions failed:\n${errors.join('\n')}`);
    }

    setSelectedIds([]);
    setShowBulkDeleteConfirm(false);
    setConfirmText('');
    fetchAttributes();
  };

  return (
    <div className="content-card">
      <div className="card-header">
        <h2 className="card-title">Attributes</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          Add Attribute
        </button>
      </div>

      {showForm && (
        <div style={{ padding: '1rem 2rem', borderBottom: '1px solid #e2e8f0' }}>
          <h3 style={{ marginBottom: '1rem' }}>{editingId ? 'Edit Attribute' : 'Add Attribute'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Name:</label>
              <input
                type="text"
                className="form-input"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Type:</label>
              <select
                className="form-select"
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="boolean">Boolean</option>
                <option value="select">Select</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Category:</label>
              <select
                className="form-select"
                value={formData.category_id}
                onChange={(e) => setFormData({...formData, category_id: e.target.value})}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {formData.type === 'select' && (
              <div className="form-group">
                <label className="form-label">Options (comma-separated):</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.options}
                  onChange={(e) => setFormData({...formData, options: e.target.value})}
                  placeholder="Option 1, Option 2, Option 3"
                />
              </div>
            )}

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={formData.required}
                  onChange={(e) => setFormData({...formData, required: e.target.checked})}
                />
                Required
              </label>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="submit" className="btn btn-primary">
                {editingId ? 'Update' : 'Create'}
              </button>
              <button type="button" className="btn btn-cancel" onClick={handleCancelEdit}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {selectedIds.length > 0 && (
        <div style={{ padding: '1rem 2rem', background: '#fef3c7', borderBottom: '1px solid #e2e8f0' }}>
          <span>{selectedIds.length} attributes selected</span>
          <button 
            className="btn btn-danger" 
            style={{ marginLeft: '1rem' }}
            onClick={handleBulkDeleteClick}
          >
            Delete Selected
          </button>
        </div>
      )}

      <div className="table-container">
        <table className="table" style={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: '50px' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '13%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={selectedIds.length === attributes.length && attributes.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
              <th>Name</th>
              <th>Type</th>
              <th>Category</th>
              <th>Required</th>
              <th>Options</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {attributes.map(attr => (
              <tr key={attr.id}>
                <td>
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={selectedIds.includes(attr.id)}
                    onChange={(e) => handleSelectItem(attr.id, e.target.checked)}
                  />
                </td>
                <td style={{ wordBreak: 'break-word' }}>{attr.name}</td>
                <td>{attr.type}</td>
                <td style={{ wordBreak: 'break-word' }}>{categories.find(c => c.id === attr.category_id)?.name || 'All'}</td>
                <td>{attr.required ? 'Yes' : 'No'}</td>
                <td style={{ wordBreak: 'break-word', fontSize: '12px' }}>{attr.options?.join(', ') || '-'}</td>
                <td>
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => handleEdit(attr)}
                    style={{ fontSize: '12px', padding: '4px 8px', marginRight: '8px' }}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => handleDelete(attr.id, attr.name)}
                    style={{ fontSize: '12px', padding: '4px 8px' }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showBulkDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Confirm Bulk Delete</h3>
            <p>You are about to delete {selectedIds.length} attributes. This action cannot be undone.</p>
            <p>Type "confirm" to proceed:</p>
            <input
              type="text"
              className="form-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type confirm"
            />
            <div style={{ marginTop: '1rem', display: 'flex', gap: '8px' }}>
              <button 
                className="btn btn-danger"
                onClick={confirmBulkDelete}
                disabled={confirmText !== 'confirm'}
              >
                Delete Attributes
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowBulkDeleteConfirm(false);
                  setConfirmText('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDeleteModal
        isOpen={deleteConfirm.show}
        title="Delete Attribute"
        message="Are you sure you want to delete this attribute? This action cannot be undone."
        itemName={deleteConfirm.name}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({show: false, id: null, name: ''})}
      />
    </div>
  );
};

export default AttributeSetup;